/**
 * AuthContext
 *
 * Provides:
 *   user        — decoded user object from the backend, or null
 *   isLoading   — true while the initial auth check is running
 *   isAuth      — shorthand: !!user
 *   login(data) — store tokens + set user
 *   logout()    — clear tokens + reset user
 *
 * Also listens for the 'auth:logout' event fired by the Axios interceptor
 * when a token refresh silently fails.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { tokenStorage } from '@/api/client'
import { getMe } from '@/api/auth'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user,      setUser]      = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // ── Restore session on app start ──────────────────────────────────
  useEffect(() => {
    const access = tokenStorage.getAccess()
    if (!access) {
      setIsLoading(false)
      return
    }
    const controller = new AbortController()
    getMe({ signal: controller.signal })
      .then(({ data }) => setUser(data))
      .catch((err) => {
        if (err.code !== 'ERR_CANCELED') tokenStorage.clearTokens()
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [])

  // ── Listen for silent-refresh failures from Axios interceptor ─────
  useEffect(() => {
    const handle = () => {
      setUser(null)
      tokenStorage.clearTokens()
    }
    window.addEventListener('auth:logout', handle)
    return () => window.removeEventListener('auth:logout', handle)
  }, [])

  // ── Public API ─────────────────────────────────────────────────────
  const login = useCallback(({ access, refresh, user: userData }) => {
    tokenStorage.setTokens({ access, refresh })
    setUser(userData)
  }, [])

  const logout = useCallback(() => {
    tokenStorage.clearTokens()
    setUser(null)
  }, [])

  const refreshUser = useCallback(() =>
    getMe().then(({ data }) => setUser(data)), [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuth: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/** @returns {{ user, isLoading, isAuth, login, logout }} */
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

